// Base64 for a picked image, in the browser.
//
// It lives apart from image.ts because that module reaches for
// `createImageBitmap`, a canvas and `toDataURL` — none of which exist outside a
// browser, which is why it is excluded from the measured set. This part is
// arithmetic over bytes, so it is testable and measured like anything else.

// How many bytes are turned into characters per call.
//
// The encoder used to build the string one character at a time. The client gate
// accepts a file up to 25 MiB, and the path this runs on is the FALLBACK — the
// one taken when the browser cannot decode the image and the original bytes are
// sent unchanged — so that was 26 million iterations of string concatenation on
// the main thread, in exactly the browser least able to absorb it. A window of
// a few thousand keeps the argument list well inside every engine's limit on
// spread arguments while cutting the iteration count by that factor.
const CHUNK_BYTES = 8192;

/** The bytes as base64, without a `data:` prefix. */
export function base64FromBuffer(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += CHUNK_BYTES) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK_BYTES));
  }
  return btoa(binary);
}

/** The inverse, for turning an encoded image back into something postable. */
export function blobFromBase64(base64: string, type: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type });
}
